/**
 * Wishlist / Favourite Functionality
 * Uses localStorage for persistence — no login required.
 *
 * localStorage key: 'joe_wishlist'
 * Value: JSON array of product objects { id, title, price, image, url }
 *
 * Events dispatched on document:
 *   wishlist:updated  →  { detail: { wishlist: [...], count: N } }
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Core Wishlist Store                                                  */
  /* ------------------------------------------------------------------ */
  var STORAGE_KEY = 'joe_wishlist';

  var Wishlist = {
    /**
     * Return the current wishlist array.
     */
    getAll: function () {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        return [];
      }
    },

    /**
     * Save the wishlist array to localStorage and dispatch update event.
     */
    _save: function (list) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (e) {
        console.warn('Wishlist: localStorage not available', e);
      }
      var event = new CustomEvent('wishlist:updated', {
        bubbles: true,
        detail: { wishlist: list, count: list.length }
      });
      document.dispatchEvent(event);
    },

    /**
     * Check whether a product ID is in the wishlist.
     */
    has: function (productId) {
      return this.getAll().some(function (p) {
        return String(p.id) === String(productId);
      });
    },

    /**
     * Add a product to the wishlist.
     * @param {Object} product  { id, title, price, image, url }
     */
    add: function (product) {
      var list = this.getAll();
      if (!this.has(product.id)) {
        var item = Object.assign({}, product, {
          id: String(product.id),
          title: product.title || '',
          price: product.price || '',
          image: product.image || product.previewImage || '',
          previewImage: product.previewImage || product.image || '',
          url: product.url || '#'
        });
        list.push(item);
        this._save(list);
      }
    },

    /**
     * Update an existing product in the wishlist by ID.
     */
    update: function (productId, updateData) {
      var list = this.getAll();
      var found = false;
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].id) === String(productId) || (list[i].key && String(list[i].key) === String(productId))) {
          Object.assign(list[i], updateData);
          found = true;
          break;
        }
      }
      if (found) {
        this._save(list);
      }
    },

    /**
     * Remove a product from the wishlist by ID.
     */
    remove: function (productId) {
      var list = this.getAll().filter(function (p) {
        return String(p.id) !== String(productId);
      });
      this._save(list);
    },

    /**
     * Toggle a product in/out of the wishlist.
     * Returns true if the product was added, false if removed.
     */
    toggle: function (product) {
      if (this.has(product.id)) {
        this.remove(product.id);
        return false;
      } else {
        this.add(product);
        return true;
      }
    },

    /**
     * Clear all items from the wishlist.
     */
    clear: function () {
      this._save([]);
    },

    /**
     * Return the number of items in the wishlist.
     */
    count: function () {
      return this.getAll().length;
    }
  };

  /* ------------------------------------------------------------------ */
  /* Wishlist Button Controller                                           */
  /* Manages heart buttons on product cards.                             */
  /* ------------------------------------------------------------------ */
  var WishlistButton = {
    init: function () {
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-wishlist-btn]');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();

        var product = {
          id:    btn.dataset.productId,
          title: btn.dataset.productTitle,
          price: btn.dataset.productPrice,
          image: btn.dataset.productImage,
          url:   btn.dataset.productUrl
        };

        var added = Wishlist.toggle(product);

        // Update this button's visual state
        WishlistButton._setActive(btn, added);

        // Pop animation
        btn.classList.remove('pop');
        // Force reflow so animation re-triggers
        void btn.offsetWidth;
        btn.classList.add('pop');
        btn.addEventListener('animationend', function () {
          btn.classList.remove('pop');
        }, { once: true });
      });

      // Sync all buttons on every wishlist update
      document.addEventListener('wishlist:updated', function () {
        WishlistButton.syncAll();
      });

      // Initial sync
      WishlistButton.syncAll();
    },

    /**
     * Sync active state for all wishlist buttons on the page.
     */
    syncAll: function () {
      document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
        var isActive = Wishlist.has(btn.dataset.productId);
        WishlistButton._setActive(btn, isActive);
      });
    },

    /**
     * Set active (filled heart) or inactive (outline heart) state.
     */
    _setActive: function (btn, active) {
      if (active) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-label', 'Remove from wishlist');
        btn.setAttribute('title', 'Remove from wishlist');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-label', 'Add to wishlist');
        btn.setAttribute('title', 'Add to wishlist');
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Wishlist Badge (Header Count Bubble)                                */
  /* ------------------------------------------------------------------ */
  var WishlistBadge = {
    init: function () {
      document.addEventListener('wishlist:updated', function (e) {
        WishlistBadge.update(e.detail.count);
      });
      WishlistBadge.update(Wishlist.count());
    },

    update: function (count) {
      document.querySelectorAll('.wishlist-header-badge').forEach(function (badge) {
        badge.textContent = count;
        if (count > 0) {
          badge.classList.add('visible');
        } else {
          badge.classList.remove('visible');
        }
      });
      if (count > 0) {
        document.body.classList.add('wishlist-has-items');
      } else {
        document.body.classList.remove('wishlist-has-items');
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Wishlist Page Renderer                                               */
  /* Renders products on /pages/wishlist from localStorage.              */
  /* ------------------------------------------------------------------ */
  var WishlistPage = {
    init: function () {
      var container = document.getElementById('wishlist-items-grid');
      var emptyEl   = document.getElementById('wishlist-empty-state');
      var clearBtn  = document.getElementById('wishlist-clear-all');
      var countEl   = document.getElementById('wishlist-item-count');

      if (!container) return; // Not on the wishlist page

      WishlistPage._render(container, emptyEl, countEl);

      // Listen for updates (e.g., remove from this page)
      document.addEventListener('wishlist:updated', function () {
        WishlistPage._render(container, emptyEl, countEl);
      });

      // Clear all
      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          if (confirm('Remove all items from your wishlist?')) {
            Wishlist.clear();
          }
        });
      }
    },

    _render: function (container, emptyEl, countEl) {
      var list = Wishlist.getAll();

      if (countEl) {
        countEl.textContent = list.length === 1
          ? '1 item'
          : list.length + ' items';
      }

      if (list.length === 0) {
        container.innerHTML = '';
        if (emptyEl) emptyEl.classList.add('visible');
        return;
      }

      if (emptyEl) emptyEl.classList.remove('visible');

      container.innerHTML = list.map(function (product) {
        var imgHtml = product.image
          ? '<img src="' + product.image + '" alt="' + WishlistPage._esc(product.title) + '" loading="lazy">'
          : '<span class="wishlist-card__no-image">🐾</span>';

        return [
          '<div class="wishlist-card" data-wishlist-card-id="' + product.id + '">',
            '<div class="wishlist-card__image-wrap">' + imgHtml + '</div>',
            '<div class="wishlist-card__body">',
              '<p class="wishlist-card__title">' + WishlistPage._esc(product.title) + '</p>',
              '<p class="wishlist-card__price">' + WishlistPage._esc(product.price) + '</p>',
              '<div class="wishlist-card__actions">',
                '<a href="' + product.url + '" class="wishlist-card__shop-btn">View Product</a>',
                '<button class="wishlist-card__remove-btn" data-remove-id="' + product.id + '" aria-label="Remove from wishlist" title="Remove from wishlist">',
                  '<svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
                '</button>',
              '</div>',
            '</div>',
          '</div>'
        ].join('');
      }).join('');

      // Bind remove buttons
      container.querySelectorAll('[data-remove-id]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var id   = btn.dataset.removeId;
          var card = container.querySelector('[data-wishlist-card-id="' + id + '"]');
          if (card) {
            card.classList.add('removing');
            card.addEventListener('animationend', function () {
              Wishlist.remove(id);
            }, { once: true });
          } else {
            Wishlist.remove(id);
          }
        });
      });
    },

    _esc: function (str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  };

  /* ------------------------------------------------------------------ */
  /* Boot                                                                 */
  /* ------------------------------------------------------------------ */
  function boot() {
    WishlistButton.init();
    WishlistBadge.init();
    WishlistPage.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose globally so Liquid / other scripts can use it if needed
  window.JoeWishlist = Wishlist;

})();
